/**
 * The Item import's Uncategorized report (piece 31; decision 2000): every item a
 * Zoho Books pull linked that stands in Uncategorized when an Items run ends,
 * with the reason no Category rule placed it, written as a CSV `File` and read
 * back from it. 🔒 The rows a person acknowledges are the rows the file holds.
 */
import type { BooksUnhomedField } from './booksSource';
import { parseCsv } from '../catalog/priceList';
import { csvCell } from '../reports/reports';
import { detailValueKey } from './itemDetails';
import { ZOHO_BOOKS_IMPORT_PATH } from './importRun';

/** 🔒 The latest report, read back from its `File` (decision 2000). */
export const ZOHO_BOOKS_UNCATEGORIZED_PATH = `${ZOHO_BOOKS_IMPORT_PATH}/uncategorized`;
/** 🔒 Acknowledge the Uncategorized Report: one `AuditEvent` on the report's `File` (decision 2000). */
export const ZOHO_BOOKS_UNCATEGORIZED_ACK_PATH = `${ZOHO_BOOKS_UNCATEGORIZED_PATH}/acknowledge`;

/** 🔒 The field of the Items run's closing audit row that keeps the report's row count (T1310). */
export const UNCATEGORIZED_ROW_COUNT_FIELD = 'uncategorizedRowCount';

/** One item the report lists. */
export interface UncategorizedRow {
  /** The item's own Zoho Books id, which Read This One Again reads. */
  sourceId: string;
  name: string;
  /** The item's SKU here. */
  sku: string;
  reason: string;
}

/** Who acknowledged a report, and when. */
export interface UncategorizedAcknowledgement {
  by: string;
  at: string;
}

/** 🔒 The latest report as the preset's answer carries it (decision 2000): its `File`, its run, its time and its acknowledgement. */
export interface UncategorizedReportSummary {
  fileId: string;
  runId: string;
  writtenAt: string;
  /** The rows the report held when its run closed, so the Items step draws its count with no read of the file; null for a report written before the count was kept (T1310). */
  rowCount: number | null;
  acknowledged: UncategorizedAcknowledgement | null;
}

/** The latest report whole, its rows read back from its `File`. */
export interface UncategorizedReportView extends UncategorizedReportSummary {
  rows: UncategorizedRow[];
}

/** A Category rule as the report reads it: the detail it reads and the value it names, folded. */
export interface UncategorizedRule {
  detailLabel: string;
  valueKey: string;
}

/** How many of an item's details a reason names before it stops. */
const REASON_DETAILS = 6;

/**
 * 🔒 Why no Category rule placed an item (decision 2000), read against the rules
 * as they stand. Four reasons: no rule is set, the item carries no detail, no
 * rule matches its details, or a rule now matches and the item takes it at its
 * next change in Zoho Books. An item no kept read describes is read again.
 */
export function uncategorizedReason(details: readonly BooksUnhomedField[] | null, rules: readonly UncategorizedRule[]): string {
  if (details === null) return 'no read of this item is kept to say which details it carries; read it again';
  if (rules.length === 0) return 'no Category rule is set yet; add one under Settings › Integrations › Import Mappings';
  if (details.length === 0) return 'the item carries no detail a Category rule can read; give it one in Zoho Books, such as its category';
  const held = new Set(details.map((detail) => `${detail.label}\u0000${detailValueKey(detail.value)}`));
  if (rules.some((rule) => held.has(`${rule.detailLabel}\u0000${rule.valueKey}`))) {
    return 'a Category rule now matches it; the item takes that category at its next change in Zoho Books';
  }
  const named = details.slice(0, REASON_DETAILS).map((detail) => `${detail.label} “${detail.value}”`);
  const more = details.length > REASON_DETAILS ? ` and ${details.length - REASON_DETAILS} more` : '';
  return `no Category rule matches its details: ${named.join(', ')}${more}`;
}

const HEAD = ['Zoho id', 'Item', 'SKU here', 'Reason'];
/** 🔒 A report with no row still says so (decision 2000), on a line naming no item. */
export const UNCATEGORIZED_NONE_LINE = 'No item stands in Uncategorized.';

/**
 * A name or SKU that already starts with a quote before a formula character, which the guard below would read as its own (T1352).
 * `\r` joins the shared `csvCell`'s own lead characters.
 */
const QUOTE_LED_FORMULA = /^'+[=+\-@\t\r]/;

/**
 * The report's own reading of a guarded cell (T1310, T1352): one quote is the
 * guard, so one comes off a value that starts with a quote before a formula
 * character. 🔒 Every CSV cell is written by `csvCell` (T2108), which writes the
 * guard before a formula-leading value; `guarded` writes one more before a value
 * already led by a quote, so a real name `'=x` comes back as `'=x` and `=x` as `=x`.
 */
function unguarded(value: string): string {
  return QUOTE_LED_FORMULA.test(value) ? value.slice(1) : value;
}

function guarded(value: string): string {
  return QUOTE_LED_FORMULA.test(value) ? `'${value}` : value;
}

/** 🔒 The report as the CSV its `File` holds: a heading, one line per item, or one line saying none (decision 2000). */
export function uncategorizedCsv(rows: readonly UncategorizedRow[]): string {
  const lines = rows.length > 0 ? rows.map((row) => [row.sourceId, guarded(row.name), guarded(row.sku), row.reason].map(csvCell).join(',')) : [['', UNCATEGORIZED_NONE_LINE, '', ''].map(csvCell).join(',')];
  return `${[HEAD.join(','), ...lines].join('\n')}\n`;
}

/** 🔒 The report's rows read back from its CSV (decision 2000); the heading and the line saying none are no row. */
export function uncategorizedRowsOf(text: string): UncategorizedRow[] {
  return parseCsv(text, { trimCells: false })
    .rows.filter((row) => (row[HEAD[0]!] ?? '') !== '')
    .map((row) => ({ sourceId: row[HEAD[0]!] ?? '', name: unguarded(row[HEAD[1]!] ?? ''), sku: unguarded(row[HEAD[2]!] ?? ''), reason: row[HEAD[3]!] ?? '' }));
}
